import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import NeonButton from '../../components/Common/Button';
import NeonInput from '../../components/Common/Input';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import './LoginPage.css';
import { useLanguage } from '../../context/LanguageContext';
import LegalConsent from '../../components/Legal/LegalConsent';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    emailOrPhone: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });

    if (errors[e.target.name] || errors.general) {
      setErrors({});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    const newErrors = {};
    if (!formData.emailOrPhone) newErrors.emailOrPhone = t('emailRequired');
    if (!formData.password) newErrors.password = t('passwordRequired');

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const isEmail = formData.emailOrPhone.includes('@');

      const credentials = {
        password: formData.password,
        ...(isEmail
          ? { email: formData.emailOrPhone }
          : { phone: formData.emailOrPhone })
      };

      const response = await apiService.login(credentials);

      if (response.success && response.data) {
        const userData = {
          id: response.data.user_id || response.data.userId,
          name: response.data.name,
          email: response.data.email,
          phone: response.data.phone,
          date_of_birth:
            response.data.date_of_birth || response.data.dateOfBirth,
          gender: response.data.gender || 'unspecified',
          place_of_study:
            response.data.place_of_study || response.data.placeOfStudy,
          role: response.data.role || 'student',
          subscription_plan: response.data.subscription_plan || (response.data.role === 'doctor' ? 'instructor' : 'starter')
        };

        login(userData, response.data.token, rememberMe);

        setErrors({ success: t('loginSuccess') });

        const destination = (response.data.role || 'student') === 'doctor'
          ? '/doctor-review'
          : '/dashboard';

        setTimeout(() => {
          navigate(destination);
        }, 1000);
      } else {
        setErrors({
          general: response.message || t('loginFailed')
        });
      }
    } catch (err) {
      const networkMessage = err.message === 'Failed to fetch'
        ? t('loginFailed')
        : err.message;
      setErrors({
        general:
          networkMessage ||
          t('loginFailed')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-blob blob-cyan"></div>
        <div className="login-blob blob-purple"></div>
      </div>

      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <span>⚕️</span>
          </div>
          <h1 className="login-title">{t('welcomeBack')}</h1>
          <p className="login-subtitle">{t('loginSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {errors.general && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              {errors.general}
            </div>
          )}

          {errors.success && (
            <div className="alert alert-success">
              <span className="alert-icon">✓</span>
              {errors.success}
            </div>
          )}

          <NeonInput
            label={t('emailOrPhone')}
            type="text"
            name="emailOrPhone"
            value={formData.emailOrPhone}
            onChange={handleChange}
            error={errors.emailOrPhone}
            placeholder="doctor@example.com or +998..."
            disabled={loading}
          />

          <NeonInput
            label={t('password')}
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            placeholder="••••••••"
            disabled={loading}
          />

          <div className="form-options">
            <label className="remember-me">
              <input
                type="checkbox"
                className="remember-checkbox"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
                disabled={loading}
              />
              <span className="remember-text">{t('rememberMe')}</span>
            </label>
            <span className="forgot-password-note">{t('passwordHelp')}</span>
          </div>

          <NeonButton
            type="submit"
            variant="primary"
            disabled={loading}
            className="login-button"
          >
            {loading ? (
              <span className="button-content">
                <span className="button-spinner"></span>
                {t('signingIn')}
              </span>
            ) : (
              t('signIn')
            )}
          </NeonButton>

          <div className="login-footer">
            <p className="signup-link">
              {t('noAccount')}{' '}
              <Link to="/register">{t('createAccount')}</Link>
            </p>
            <LegalConsent variant="login" />
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
